import { TableCellInputType } from "@components/data-table/types";
import { WpFieldConfig } from "@hooks/use-config/types";
import { WpTaxonomyValue } from "@hooks/use-get/taxonomy-values";

/**
 * Field type checkers - used by both assets and tickets
 */

export const isFieldBoolean = (field: WpFieldConfig) => {
  return !!(field?.type === "radio");
};

export const isFieldTaxonomy = (field: WpFieldConfig) => {
  return !!(field?.type === "taxonomy");
};

export const isFieldSelect = (field: WpFieldConfig) => {
  return !!(field?.type === "select");
};

export const isFieldForm = (field: WpFieldConfig) => {
  return !!(field?.type === "group" && (field?.fields ?? [])?.length > 0);
};

export const isFieldTextInput = (field: WpFieldConfig) => {
  return !!(
    field?.type === "text" ||
    field?.type === "number" ||
    field?.type === "email" ||
    field?.type === "url"
  );
};

export const isFieldDate = (field: WpFieldConfig) => {
  return !!(
    field?.type === "date" ||
    field?.type === "datetime" ||
    field?.type === "time"
  );
};

export const isFieldDateTime = (field: WpFieldConfig) => {
  return !!(field?.type === "datetime");
};

export const isFieldMultiLine = (field: WpFieldConfig) => {
  return !!(field?.type === "textarea" || field?.type === "wysiwyg");
};

export const isFieldFileUpload = (field: WpFieldConfig) => {
  return !!(field?.type === "file_upload");
};

export const isFieldUserInput = (field: WpFieldConfig) => {
  return !!(field?.type === "user");
};

/**
 * Convert field config to TableCellInputType
 */
export const getFieldInputType = (field: WpFieldConfig) => {
  if (isFieldBoolean(field)) return TableCellInputType.Boolean;
  if (isFieldTextInput(field)) return TableCellInputType.Text;
  if (isFieldDate(field)) return TableCellInputType.Date;
  if (isFieldDateTime(field)) return TableCellInputType.DateTime;
  if (isFieldMultiLine(field)) return TableCellInputType.MultiLine;
  if (isFieldFileUpload(field)) return TableCellInputType.FileUpload;
  if (isFieldUserInput(field)) return TableCellInputType.UserInput;
  if (isFieldTaxonomy(field)) return TableCellInputType.Select;
  if (isFieldSelect(field)) return TableCellInputType.Select;
  if (isFieldForm(field)) return TableCellInputType.Form;
  return TableCellInputType.Text; // Default fallback
};

/**
 * Check if a field is filterable
 */
export const isFieldFilterable = (field: WpFieldConfig | undefined) => {
  if (!field) return false;
  const fieldType = getFieldInputType(field);
  return (
    ![
      TableCellInputType.Form,
      TableCellInputType.UserInput,
    ].includes(fieldType)
  );
};

/**
 * Check if a field type can be used for visualization
 */
export const isFieldVisualization = (inputType: TableCellInputType) => {
  return ![
    TableCellInputType.Form,
    TableCellInputType.Date,
    TableCellInputType.DateTime,
    TableCellInputType.MultiLine,
  ].includes(inputType);
};

/**
 * Extract options from field config (supports both taxonomy and static options)
 */
export const getOptionsFromSelectFieldConfig = (field: WpFieldConfig) => {
  const currentConfigTaxonomyOptions = (
    field?.taxonomy as WpTaxonomyValue[]
  )?.map((option) => ({
    label: option.name,
    value: option.id,
  }));
  const currentConfigOptions = field?.options ?? null;

  return (currentConfigTaxonomyOptions?.length ?? 0) > 0
    ? currentConfigTaxonomyOptions
    : currentConfigOptions;
};

/**
 * Generic function to extract value from an item's meta_box
 */
export const getValueFromMetaBox = (
  item: any,
  key: string
) => {
  try {
    const value = ((item?.meta_box as any) ?? {})[key];
    if (Array.isArray(value)) {
      return typeof value[0] === "object"
        ? value.map((item) => JSON.stringify(item)).join(", ")
        : value.join(", ");
    } else if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  } catch (error) {
    return null;
  }
};